'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Chip, Spinner, ModalContent, ModalHeader, ModalBody } from '@heroui/react';
import { IdView, ResponsiveModal, Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { OnlyFansIc } from '../../../../components/icons';
import { AvailableCampaign, AvailableCplCampaign } from '../types';
import { useIsMobile } from '../../../../hooks/useMobile';

interface CampaignSearchSelectProps {
  placeholder?: string;
  campaigns: AvailableCampaign[];
  cplCampaigns?: AvailableCplCampaign[];
  selectedKey: string;
  onSelectionChange: (value: string) => void;
  isLoading?: boolean;
  isCplLoading?: boolean;
  hasMoreCampaigns?: boolean;
  hasMoreCplCampaigns?: boolean;
  campaignsTotalCount?: number;
  cplCampaignsTotalCount?: number;
  onLoadMoreCampaigns?: () => void;
  onLoadMoreCplCampaigns?: () => void;
  onSearchCampaigns?: (query: string) => void;
  startContent?: React.ReactNode;
}

const CampaignSearchSelect: React.FC<CampaignSearchSelectProps> = ({
  placeholder = 'Кампания',
  campaigns,
  cplCampaigns = [],
  selectedKey,
  onSelectionChange,
  isLoading = false,
  isCplLoading = false,
  hasMoreCampaigns = false,
  hasMoreCplCampaigns = false,
  campaignsTotalCount = 0,
  cplCampaignsTotalCount = 0,
  onLoadMoreCampaigns,
  onLoadMoreCplCampaigns,
  onSearchCampaigns,
  startContent,
}) => {
  const { t } = useTranslations();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const revshareScrollRef = useRef<HTMLDivElement>(null);
  const cplScrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Find selected campaign in both lists
  const selectedRevshare = campaigns.find((c) => c.id === selectedKey);
  const selectedCpl = cplCampaigns.find((c) => c.id === selectedKey);
  const selectedCampaign = selectedRevshare || selectedCpl;

  // Close dropdown when clicking outside (desktop only)
  useEffect(() => {
    if (isMobile || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile, isOpen]);

  const handleToggle = () => {
    const next = !isOpen;
    if (next && !hasBeenOpened) setHasBeenOpened(true);
    if (next) {
      if (searchQuery) {
        setSearchQuery('');
        onSearchCampaigns?.('');
      }
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
    setIsOpen(next);
  };

  const handleSelect = (campaignId: string) => {
    onSelectionChange(campaignId);
    setIsOpen(false);
  };

  // Infinite scroll handler
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>, type: 'revshare' | 'cpl') => {
      const el = e.currentTarget;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      if (!nearBottom) return;

      if (type === 'revshare' && hasMoreCampaigns && !isLoading) {
        onLoadMoreCampaigns?.();
      } else if (type === 'cpl' && hasMoreCplCampaigns && !isCplLoading) {
        onLoadMoreCplCampaigns?.();
      }
    },
    [hasMoreCampaigns, hasMoreCplCampaigns, isLoading, isCplLoading, onLoadMoreCampaigns, onLoadMoreCplCampaigns]
  );

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Get selected campaign display for trigger button
  const getSelectedDisplay = () => {
    if (!selectedCampaign) return null;
    const shortDisplay = selectedCampaign.shortId.slice(-4);
    const isCpl = !!selectedCpl;
    return (
      <div className="flex items-center gap-2">
        <Chip
          size="sm"
          classNames={{
            base: `h-5 px-1.5 ${isCpl ? 'bg-[#E0EDFC]' : 'bg-[#F1E8FF]'}`,
            content: `text-[10px] font-semibold ${isCpl ? 'text-[#006FEE]' : 'text-[#9675CE]'} px-0`,
          }}
        >
          {isCpl ? 'CPL' : 'RevShare'}
        </Chip>
        <span className="text-sm text-auth-ink">#{shortDisplay}</span>
        <span className="text-xs font-semibold text-auth-ink">{selectedCampaign.modelName}</span>
      </div>
    );
  };

  // Shared campaign item renderer
  const renderCampaignItem = (campaign: AvailableCampaign | AvailableCplCampaign) => (
    <button
      key={campaign.id}
      type="button"
      onClick={() => handleSelect(campaign.id)}
      className={`w-full flex items-center gap-2 h-8 px-2 py-1.5 rounded-3 text-left ${
        selectedKey === campaign.id ? 'bg-[#EEEEEF]' : 'hover:bg-auth-surface'
      }`}
    >
      <IdView textToCopy={campaign.shortId}>{campaign.shortId.slice(-4)}</IdView>
      <span className="text-xs font-semibold text-auth-ink truncate">{campaign.modelName}</span>
      <Chip
        size="sm"
        variant="bordered"
        classNames={{
          base: 'h-6 border-[1.4px] border-auth-strength-none rounded-3.5 px-1 shrink-0',
          content: 'flex items-center gap-0.5 px-1 text-xs text-auth-ink',
        }}
        startContent={<OnlyFansIc size={14} />}
      >
        {formatDate(campaign.createdAt)}
      </Chip>
    </button>
  );

  // Mobile campaign item (slightly different styling)
  const renderMobileCampaignItem = (campaign: AvailableCampaign | AvailableCplCampaign) => (
    <button
      key={campaign.id}
      type="button"
      onClick={() => handleSelect(campaign.id)}
      className={`w-full flex md:flex-row flex-col md:items-center gap-2 md:h-8 px-2 py-1.5 rounded-3 border border-solid border-[#EEEEEF] text-left ${
        selectedKey === campaign.id ? 'bg-[#EEEEEF]' : 'hover:bg-auth-surface'
      }`}
    >
      <div className="flex items-center gap-2">
        <IdView textToCopy={campaign.shortId}>{campaign.shortId.slice(-4)}</IdView>
        <span className="text-xs font-semibold text-auth-ink">{campaign.modelName}</span>
      </div>
      <Chip
        size="sm"
        variant="bordered"
        classNames={{
          base: 'h-6 border-[1.4px] border-auth-strength-none rounded-3.5 px-1',
          content: 'flex items-center gap-0.5 px-1 text-xs text-auth-ink',
        }}
        startContent={<OnlyFansIc size={14} />}
      >
        {formatDate(campaign.createdAt)}
      </Chip>
    </button>
  );

  // Column content for desktop
  const renderColumnContent = (
    items: (AvailableCampaign | AvailableCplCampaign)[],
    loading: boolean,
    hasMore: boolean
  ) => {
    if (loading && items.length === 0) {
      return (
        <div className="flex items-center justify-center py-4 text-partner">
          <Spinner size="sm" color="current" />
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <div className="px-2 py-2.5 text-auth-muted text-xs">
          {t('partner.revshare.income.filters.noCampaigns') || 'Нет кампаний'}
        </div>
      );
    }
    return (
      <>
        {items.map(renderCampaignItem)}
        {loading && (
          <div className="flex items-center justify-center py-2 text-partner">
            <Spinner size="sm" color="current" />
          </div>
        )}
      </>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger button */}
      <button
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-1.5 h-9 px-3 py-1.5 bg-auth-surface rounded-2 hover:bg-[#EEEEEF] transition-colors"
      >
        {startContent}
        {selectedCampaign ? (
          getSelectedDisplay()
        ) : (
          <span className="text-sm text-auth-muted">{placeholder}</span>
        )}
      </button>

      {/* Desktop Dropdown - pre-rendered, toggled via CSS */}
      {!isMobile && hasBeenOpened && (
        <div
          className={`absolute top-[calc(100%+8px)] left-0 z-50 bg-white border border-[#E0E0E0] rounded-3 shadow-[0rem_0rem_1.5rem_0rem_rgba(0,0,0,0.1)] p-2 min-w-[34rem] transition-opacity duration-100 ${
            isOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
        >
          {/* Search input */}
          <div className="relative mb-2">
            <svg
              className="absolute left-2 top-1/2 -translate-y-1/2 text-auth-muted"
              width="14"
              height="14"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.5 17.5L13.875 13.875"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                onSearchCampaigns?.(e.target.value);
              }}
              placeholder={t('partner.revshare.income.filters.searchCampaigns')}
              className="w-full h-8 pl-7 pr-2 text-xs bg-auth-surface rounded-2 border-none outline-none text-auth-ink placeholder:text-auth-muted"
            />
          </div>

          {/* "All" option */}
          <button
            type="button"
            onClick={() => handleSelect('all')}
            className={`w-full flex items-center h-8 px-2 rounded-2 text-left ${
              selectedKey === 'all' || selectedKey === '' ? 'bg-[#EEEEEF]' : 'hover:bg-auth-surface'
            }`}
          >
            <Typography variant="body2" className="text-auth-ink">
              {t('partner.revshare.income.filters.allCampaigns')}
            </Typography>
          </button>

          {/* Two-column layout */}
          <div className="flex gap-2 mt-2">
            {/* RevShare column */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 px-2 mb-1.5">
                <Chip
                  size="sm"
                  classNames={{
                    base: 'h-5 px-1.5 bg-[#F1E8FF]',
                    content: 'text-[10px] font-semibold text-[#9675CE] px-0',
                  }}
                >
                  RevShare
                </Chip>
                <span className="text-xs text-auth-muted">({campaignsTotalCount})</span>
              </div>
              <div
                ref={revshareScrollRef}
                className="flex flex-col max-h-[16rem] overflow-y-auto scroller-none"
                onScroll={(e) => handleScroll(e, 'revshare')}
              >
                {renderColumnContent(campaigns, isLoading, hasMoreCampaigns)}
              </div>
            </div>

            {/* Divider */}
            <div className="w-px bg-[#E0E0E0] shrink-0" />

            {/* CPL column */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 px-2 mb-1.5">
                <Chip
                  size="sm"
                  classNames={{
                    base: 'h-5 px-1.5 bg-[#E0EDFC]',
                    content: 'text-[10px] font-semibold text-[#006FEE] px-0',
                  }}
                >
                  CPL
                </Chip>
                <span className="text-xs text-auth-muted">({cplCampaignsTotalCount})</span>
              </div>
              <div
                ref={cplScrollRef}
                className="flex flex-col max-h-[16rem] overflow-y-auto scroller-none"
                onScroll={(e) => handleScroll(e, 'cpl')}
              >
                {renderColumnContent(cplCampaigns, isCplLoading, hasMoreCplCampaigns)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Modal */}
      {isOpen && isMobile && (
        <ResponsiveModal isOpen={isOpen} onClose={() => setIsOpen(false)} scrollBehavior="inside">
          <ModalContent>
            <ModalHeader>
              <button
                type="button"
                onClick={() => handleSelect('all')}
                className={`w-full flex items-center md:h-8 p-2 px-4 rounded-2 text-left ${
                  selectedKey === '' || selectedKey === 'all'
                    ? 'bg-[#EEEEEF]'
                    : 'hover:bg-auth-surface'
                }`}
              >
                <Typography variant="body2" className="text-auth-ink">
                  {t('partner.revshare.income.filters.allCampaigns')}
                </Typography>
              </button>
            </ModalHeader>

            <ModalBody className="p-4 pt-0">
              {/* Search input */}
              <div className="relative mb-3">
                <svg
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-auth-muted"
                  width="14"
                  height="14"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M17.5 17.5L13.875 13.875"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('partner.revshare.income.filters.searchById') || 'Поиск по ID или имени'}
                  className="w-full h-9 pl-7 pr-2 text-sm bg-auth-surface rounded-2 border-none outline-none text-auth-ink placeholder:text-auth-muted"
                />
              </div>

              {/* RevShare section */}
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <Chip
                    size="sm"
                    classNames={{
                      base: 'h-5 px-1.5 bg-[#F1E8FF]',
                      content: 'text-[10px] font-semibold text-[#9675CE] px-0',
                    }}
                  >
                    RevShare
                  </Chip>
                  <span className="text-xs text-auth-muted">({campaignsTotalCount})</span>
                </div>
                <div className="flex flex-col gap-1">
                  {isLoading && campaigns.length === 0 ? (
                    <div className="flex items-center justify-center py-4 text-partner">
                      <Spinner size="sm" color="current" />
                    </div>
                  ) : campaigns.length === 0 ? (
                    <div className="px-2 py-2.5 text-auth-muted text-sm">
                      {t('partner.revshare.income.filters.noCampaigns') || 'Нет кампаний'}
                    </div>
                  ) : (
                    <>
                      {campaigns.map(renderMobileCampaignItem)}
                      {hasMoreCampaigns && (
                        <button
                          type="button"
                          onClick={onLoadMoreCampaigns}
                          className="w-full flex items-center justify-center py-2 text-xs text-auth-muted hover:text-auth-ink"
                        >
                          {isLoading ? <span className="text-partner"><Spinner size="sm" color="current" /></span> : t('global.common.loadMore') || 'Загрузить ещё'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* CPL section */}
              <div>
                <div className="flex items-center gap-1.5 mb-2">
                  <Chip
                    size="sm"
                    classNames={{
                      base: 'h-5 px-1.5 bg-[#E0EDFC]',
                      content: 'text-[10px] font-semibold text-[#006FEE] px-0',
                    }}
                  >
                    CPL
                  </Chip>
                  <span className="text-xs text-auth-muted">({cplCampaignsTotalCount})</span>
                </div>
                <div className="flex flex-col gap-1">
                  {isCplLoading && cplCampaigns.length === 0 ? (
                    <div className="flex items-center justify-center py-4 text-partner">
                      <Spinner size="sm" color="current" />
                    </div>
                  ) : cplCampaigns.length === 0 ? (
                    <div className="px-2 py-2.5 text-auth-muted text-sm">
                      {t('partner.revshare.income.filters.noCampaigns') || 'Нет кампаний'}
                    </div>
                  ) : (
                    <>
                      {cplCampaigns.map(renderMobileCampaignItem)}
                      {hasMoreCplCampaigns && (
                        <button
                          type="button"
                          onClick={onLoadMoreCplCampaigns}
                          className="w-full flex items-center justify-center py-2 text-xs text-auth-muted hover:text-auth-ink"
                        >
                          {isCplLoading ? <span className="text-partner"><Spinner size="sm" color="current" /></span> : t('global.common.loadMore') || 'Загрузить ещё'}
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </ModalBody>
          </ModalContent>
        </ResponsiveModal>
      )}
    </div>
  );
};

export default CampaignSearchSelect;
